/**
 * Flow — the options tape, queried server-side.
 *
 * Rebuilt against `/api/options-flow/flow` after an audit found the old view fetched a window of
 * the newest prints and filtered it in the browser. With ~206M rows returned newest-first, that
 * window is routinely one symbol inside one second, so every filter looked broken and every
 * "category" collapsed to a single ticker.
 *
 * Here, every control is a query parameter: QuestDB does the filtering, the sorting, the counting
 * and the paging. Categorisation comes from a GROUP BY over the whole match set rather than from
 * tallying a page. Nothing on this screen is computed from a partial fetch, which is what makes
 * the numbers mean what they say.
 *
 * 2026-10-18: the Top Premium Board (Smart Flow) was retired into this view. Its defaults and
 * verdict readings became curated presets (flowtape/tapePresets), its field-filter builder became
 * the custom rules (server-side where a parameter exists, marked client-side where not), and its
 * 30 s poll, contracts board and favourites came across as the Live toggle, the Contracts view and
 * the star column. Users save their own presets beside the curated ones.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './RtlScannerView.css';
import './FlowTapeView.css';
import './FlowTapeView.ds.css';
import { isDesignSystemOn } from '../theme/mmecDsPreview';
import ToolState from './mmec/ToolState';
import { useHistoricalModal } from '../contexts/HistoricalModalContext';
import AlertRulesPanel from './alerts/AlertRulesPanel';
import { useAlertRules } from '../hooks/useAlertRules';
import { flattenRawRecord, FLOW_TAPE_SOURCE, SMART_FLOW_SOURCE } from '../utils/alertSources';
import { tapePrintToTile } from '../utils/liveTapeTiles';
import type { CustomFilter } from '../utils/rtlScannerFlowRows';
import PresetsRow from './flowtape/PresetsRow';
import ContractsTable from './flowtape/ContractsTable';
import RuleBuilder from './flowtape/RuleBuilder';
import { CONTRACT_BOARD_LIMIT, ContractRow, fetchContractRows } from './flowtape/contractBoard';
import { CONTRACT_ROW_FIELDS, matchesRules, planRules } from './flowtape/tapeRules';
import {
  contractKeyOf,
  CURATED_PRESETS,
  FILTER_KEYS,
  loadFavourites,
  loadUserPresets,
  newPresetId,
  saveUserPresets,
  settingsKey,
  TapePreset,
  TapeSettings,
  TapeView,
  toggleFavourite,
  VerdictFilter,
} from './flowtape/tapePresets';
import { ASSET_CLASS_OPTIONS } from '../utils/assetClass';
import { CAP_TIER_OPTIONS } from '../utils/capTier';
import { describeWindow, resolveTimeWindow, TIME_WINDOWS, TimeWindowId } from '../utils/timeWindows';
import type { ContractHistoricalParams } from '../types/contractHistorical';
import {
  fetchFlowAggregate,
  fetchFlowTape,
  FLOW_GROUP_FIELDS,
  FLOW_MONEYNESS_OPTIONS,
  FLOW_SORT_FIELDS,
  FLOW_UNAVAILABLE_FILTERS,
  FlowAggregateBucket,
  FlowTapePage,
  FlowTapeQuery,
  FlowTapeRow,
  formatPremium,
  formatTapeTime,
} from '../utils/flowTapeApi';

const PAGE_SIZES = [50, 100, 250, 500];
const QUERY_DEBOUNCE_MS = 400;
/** Live refresh cadence, as the Top Premium Board polled. */
const POLL_MS = 30000;
const ROLLING_WINDOWS = [
  { minutes: 30, label: '30 min' },
  { minutes: 120, label: '2 h' },
];
const VERDICT_CHIPS: { value: Exclude<VerdictFilter, ''>; label: string; title: string }[] = [
  { value: 'Bought', label: 'Bought', title: '≥55% of the contract’s premium printed at the ask' },
  { value: 'Sold', label: 'Sold', title: '≤45% of the contract’s premium printed at the ask' },
  { value: 'Multi-leg', label: 'Multi-leg', title: '≥60% of labelled volume printed as a complex order' },
];

function rollingFrom(minutes: number): string {
  return new Date(Date.now() - minutes * 60000).toISOString();
}

let ruleSeq = 0;
const newRuleId = () => `r${(ruleSeq += 1)}`;

/** The window bounds are ISO instants ("…T00:00:00.000Z"); a datetime-local input only
 *  accepts "yyyy-MM-ddTHH:mm". The fields are labelled UTC, so show the UTC wall time and
 *  turn an edit back into a UTC instant. */
function isoToUtcInput(v: string | undefined): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 16);
}

function utcInputToIso(v: string): string {
  if (!v) return '';
  const d = new Date(`${v}:00Z`);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

const EMPTY_QUERY: FlowTapeQuery = {
  ticker: '',
  search: '',
  side: '',
  optionType: '',
  minPremium: '',
  maxPremium: '',
  minSize: '',
  minStrike: '',
  maxStrike: '',
  minDte: '',
  maxDte: '',
  expiry: '',
  moneyness: '',
  underlyingClass: '',
  capTier: '',
  fromTs: '',
  toTs: '',
};

/** Direction from the tape side, using the platform's NBBO convention (ask = buy, bid = sell). */
function sideClass(side: string): 'buy' | 'sell' | 'unk' {
  const s = side.trim().toLowerCase();
  if (s === 'ask') return 'buy';
  if (s === 'bid') return 'sell';
  return 'unk';
}

function historicalParams(r: FlowTapeRow): ContractHistoricalParams | null {
  if (!r.ticker || r.strike == null || !r.expiry) return null;
  return {
    underlying: r.ticker,
    strike: r.strike,
    expiry: r.expiry,
    optionType: r.optionType === 'P' ? 'PUT' : 'CALL',
    source: 'volume',
  };
}

export interface FlowTapeViewProps {
  simpleLayout?: boolean;
}

function resolveBounds(s: Pick<TapeSettings, 'window' | 'rollingMinutes' | 'fromTs' | 'toTs'>): { fromTs: string; toTs: string } {
  if (s.rollingMinutes) return { fromTs: rollingFrom(s.rollingMinutes), toTs: '' };
  if (s.window) {
    const w = resolveTimeWindow(s.window);
    return { fromTs: w.fromTs ?? '', toTs: w.toTs ?? '' };
  }
  return { fromTs: s.fromTs, toTs: s.toTs };
}

const DEFAULT_PRESET = CURATED_PRESETS[0];

const FlowTapeView: React.FC<FlowTapeViewProps> = ({ simpleLayout = false }) => {
  const [query, setQuery] = useState<FlowTapeQuery>(() => ({
    ...EMPTY_QUERY,
    ...DEFAULT_PRESET.settings.filters,
    ...resolveBounds(DEFAULT_PRESET.settings),
  }));
  // Premium, not time: the newest hundred prints on this tape are one or two contracts
  // repeating, which reads as a broken screen. Time remains one click away.
  const [sortBy, setSortBy] = useState(DEFAULT_PRESET.settings.sortBy);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(DEFAULT_PRESET.settings.sortDir);
  const [pageSize, setPageSize] = useState(100);
  const [offset, setOffset] = useState(0);

  const [page, setPage] = useState<FlowTapePage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [alertsOpen, setAlertsOpen] = useState(false);
  const [alertSourceId, setAlertSourceId] = useState<'flow-tape' | 'smart-flow'>('flow-tape');
  const [groupBy, setGroupBy] = useState('ticker');
  /** Defaults to the last day that actually traded, so a weekend shows Friday, not the
      thin off-hours feed. Picking an explicit From/To clears it. */
  const [timeWindow, setTimeWindow] = useState<TimeWindowId | ''>(DEFAULT_PRESET.settings.window);
  /** Rolling look-back; re-resolved on every live refresh so "last 30 min" keeps moving. */
  const [rollingMinutes, setRollingMinutes] = useState<number | null>(DEFAULT_PRESET.settings.rollingMinutes);
  const [buckets, setBuckets] = useState<FlowAggregateBucket[]>([]);
  const [bucketsLoading, setBucketsLoading] = useState(false);

  // ── presets, rules, view, live, favourites ───────────────────────────────
  const [view, setView] = useState<TapeView>(DEFAULT_PRESET.settings.view);
  const [live, setLive] = useState(DEFAULT_PRESET.settings.live);
  const [rules, setRules] = useState<CustomFilter[]>([]);
  const [verdict, setVerdict] = useState<VerdictFilter>('');
  const [favOnly, setFavOnly] = useState(false);
  const [favs, setFavs] = useState<Set<string>>(() => loadFavourites());
  const [userPresets, setUserPresets] = useState<TapePreset[]>(() => loadUserPresets());
  const [basePresetId, setBasePresetId] = useState<string | null>(DEFAULT_PRESET.id);
  const [refreshTick, setRefreshTick] = useState(0);
  const [lastLoaded, setLastLoaded] = useState<Date | null>(null);
  const [contractRows, setContractRows] = useState<ContractRow[]>([]);

  const { openHistorical } = useHistoricalModal();

  // Server-mappable rules fold into the request; the rest filter the page client-side.
  const plan = useMemo(() => planRules(query, rules), [query, rules]);
  // The query object is rebuilt on every keystroke; this key lets effects depend on its value.
  const queryKey = JSON.stringify(plan.query);

  const pageAbort = useRef<AbortController | null>(null);
  const aggAbort = useRef<AbortController | null>(null);
  const boardAbort = useRef<AbortController | null>(null);
  /** The tick the last request ran on, so a live refresh does not flash "Querying…". */
  const lastTickRef = useRef(0);

  const applyWindow = (w: TimeWindowId) => {
    const { fromTs, toTs } = resolveTimeWindow(w);
    setTimeWindow(w);
    setRollingMinutes(null);
    setQuery((prev) => ({ ...prev, fromTs: fromTs ?? '', toTs: toTs ?? '' }));
    setOffset(0);
  };

  const applyRolling = (minutes: number) => {
    setRollingMinutes(minutes);
    setTimeWindow('');
    setQuery((prev) => ({ ...prev, fromTs: rollingFrom(minutes), toTs: '' }));
    setOffset(0);
  };

  const runQuery = useCallback(
    async (q: FlowTapeQuery, off: number, size: number, sb: string, sd: 'asc' | 'desc', quiet: boolean) => {
      pageAbort.current?.abort();
      const controller = new AbortController();
      pageAbort.current = controller;
      if (!quiet) setLoading(true);
      setError(null);
      try {
        const res = await fetchFlowTape(
          { ...q, sortBy: sb, sortDir: sd, limit: size, offset: off },
          controller.signal,
        );
        setPage(res);
        setLastLoaded(new Date());
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') return;
        if (!quiet) setPage(null);
        setError(e instanceof Error ? e.message : 'Flow tape request failed');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [],
  );

  // Filter/sort/page changes re-query the server.
  useEffect(() => {
    if (view !== 'prints') return undefined;
    const q = JSON.parse(queryKey) as FlowTapeQuery;
    const quiet = refreshTick !== lastTickRef.current;
    const t = window.setTimeout(() => {
      lastTickRef.current = refreshTick;
      runQuery(q, offset, pageSize, sortBy, sortDir, quiet);
    }, QUERY_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [queryKey, offset, pageSize, sortBy, sortDir, runQuery, view, refreshTick]);

  // Contracts view: premium summed per contract over the same effective query.
  useEffect(() => {
    if (view !== 'contracts') return undefined;
    const q = JSON.parse(queryKey) as FlowTapeQuery;
    const quiet = refreshTick !== lastTickRef.current;
    const t = window.setTimeout(async () => {
      lastTickRef.current = refreshTick;
      boardAbort.current?.abort();
      const controller = new AbortController();
      boardAbort.current = controller;
      if (!quiet) setLoading(true);
      setError(null);
      try {
        setContractRows(await fetchContractRows(q, CONTRACT_BOARD_LIMIT, controller.signal));
        setLastLoaded(new Date());
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') return;
        if (!quiet) setContractRows([]);
        setError(e instanceof Error ? e.message : 'Contract board request failed');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, QUERY_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [queryKey, view, refreshTick]);

  // Categorisation follows the same filters, so the buckets always describe what is on screen.
  useEffect(() => {
    const q = JSON.parse(queryKey) as FlowTapeQuery;
    const quiet = refreshTick > 0;
    const t = window.setTimeout(async () => {
      aggAbort.current?.abort();
      const controller = new AbortController();
      aggAbort.current = controller;
      if (!quiet) setBucketsLoading(true);
      try {
        setBuckets(await fetchFlowAggregate(q, groupBy, 25, controller.signal));
      } catch {
        if (!controller.signal.aborted) setBuckets([]);
      } finally {
        if (!controller.signal.aborted) setBucketsLoading(false);
      }
    }, QUERY_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [queryKey, groupBy, refreshTick]);

  useEffect(() => () => {
    pageAbort.current?.abort();
    aggAbort.current?.abort();
    boardAbort.current?.abort();
  }, []);

  // ── live refresh ──────────────────────────────────────────────────────────
  /** Paging away from the first page pauses Live: rows shifting under page 3 are unreadable. */
  const livePausedForPaging = live && view === 'prints' && offset > 0;
  const rollingRef = useRef(rollingMinutes);
  rollingRef.current = rollingMinutes;
  useEffect(() => {
    if (!live || livePausedForPaging) return undefined;
    const tick = () => {
      if (document.hidden) return;
      const m = rollingRef.current;
      if (m) setQuery((prev) => ({ ...prev, fromTs: rollingFrom(m), toTs: '' }));
      setRefreshTick((n) => n + 1);
    };
    const timer = window.setInterval(tick, POLL_MS);
    // Coming back to a hidden tab catches up at once rather than up to 30 s later.
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [live, livePausedForPaging]);

  // ── what is on screen ─────────────────────────────────────────────────────
  const rowsAll = useMemo(() => page?.rows ?? [], [page]);
  const rowFavKey = (r: FlowTapeRow) => contractKeyOf(r.raw, r);
  const rows = useMemo(
    () =>
      rowsAll.filter(
        (r) => matchesRules(r.raw, plan.client) && (!favOnly || favs.has(contractKeyOf(r.raw, r))),
      ),
    [rowsAll, plan.client, favOnly, favs],
  );
  const contractClientRules = useMemo(
    () => plan.client.filter((r) => CONTRACT_ROW_FIELDS.has(r.field)),
    [plan.client],
  );
  const contracts = useMemo(
    () =>
      contractRows.filter((c) => {
        if (!matchesRules(c.raw, contractClientRules)) return false;
        if (favOnly && !favs.has(c.contract)) return false;
        if (verdict === 'Bought' && c.verdict.direction !== 'Bought') return false;
        if (verdict === 'Sold' && c.verdict.direction !== 'Sold') return false;
        if (verdict === 'Multi-leg' && c.verdict.structure !== 'Multi-leg') return false;
        return true;
      }),
    [contractRows, contractClientRules, favOnly, favs, verdict],
  );

  // Rules see what the view shows, so an alert respects the filters that produced it.
  useAlertRules(FLOW_TAPE_SOURCE, (view === 'prints' ? rows : []) as unknown as Record<string, unknown>[], {
    paused: loading,
  });
  // Rules written on the retired Top Premium Board keep firing, against the same tile shape.
  const tileRecords = useMemo(() => {
    if (view === 'contracts') {
      return contracts.map((c) =>
        flattenRawRecord({
          t: c.ticker, rv: null, side: c.optionType, strike: c.strike, exp: c.expiry, dte: c.dte,
          prem: c.premium, flow: 'UNK', money: '----', mc: 'NA', fl: 'Aggregate', color: 'unk',
          horizon_tml: null, id: c.contract, raw: { ...c.raw, Prints: c.prints },
        }),
      );
    }
    const out: Record<string, unknown>[] = [];
    rows.forEach((r, i) => {
      const tile = tapePrintToTile(r.raw, i);
      if (tile) out.push(flattenRawRecord(tile as unknown as Record<string, unknown>));
    });
    return out;
  }, [view, contracts, rows]);
  useAlertRules(SMART_FLOW_SOURCE, tileRecords, { paused: loading });

  /** Any filter edit returns to the first page — page 400 of the old result set is meaningless. */
  const setField = (key: keyof FlowTapeQuery, value: string) => {
    setQuery((prev) => ({ ...prev, [key]: value }));
    setOffset(0);
  };

  // ── presets ───────────────────────────────────────────────────────────────
  const currentSettings: TapeSettings = useMemo(() => {
    const filters: TapeSettings['filters'] = {};
    for (const k of FILTER_KEYS) {
      const v = String(query[k] ?? '').trim();
      if (v) filters[k] = v;
    }
    return {
      filters,
      window: rollingMinutes ? '' : timeWindow,
      rollingMinutes,
      fromTs: rollingMinutes || timeWindow ? '' : query.fromTs ?? '',
      toTs: rollingMinutes || timeWindow ? '' : query.toTs ?? '',
      sortBy,
      sortDir,
      live,
      view,
      rules: rules.filter((r) => r.value.trim()).map(({ field, op, value }) => ({ field, op, value })),
      verdict: view === 'contracts' ? verdict : '',
    };
  }, [query, timeWindow, rollingMinutes, sortBy, sortDir, live, view, rules, verdict]);

  const allPresets = useMemo(() => [...CURATED_PRESETS, ...userPresets], [userPresets]);
  const activePresetId = useMemo(() => {
    const key = settingsKey(currentSettings);
    const base = allPresets.find((p) => p.id === basePresetId);
    if (base && settingsKey(base.settings) === key) return base.id;
    return allPresets.find((p) => settingsKey(p.settings) === key)?.id ?? null;
  }, [currentSettings, allPresets, basePresetId]);
  const baseName = allPresets.find((p) => p.id === basePresetId)?.name ?? null;

  const applyPreset = (p: TapePreset) => {
    const s = p.settings;
    setQuery({ ...EMPTY_QUERY, ...s.filters, ...resolveBounds(s) });
    setTimeWindow(s.rollingMinutes ? '' : s.window);
    setRollingMinutes(s.rollingMinutes);
    setSortBy(s.sortBy);
    setSortDir(s.sortDir);
    setLive(s.live);
    setView(s.view);
    setRules(s.rules.map((r) => ({ ...r, id: newRuleId() })));
    setVerdict(s.verdict);
    setOffset(0);
    setBasePresetId(p.id);
  };

  const storeUserPresets = (next: TapePreset[]) => {
    setUserPresets(next);
    saveUserPresets(next);
  };

  const savePreset = (name: string) => {
    const p: TapePreset = { id: newPresetId(), name, description: '', settings: currentSettings };
    storeUserPresets([...userPresets, p]);
    setBasePresetId(p.id);
  };

  const resetAll = () => {
    applyPreset(DEFAULT_PRESET);
    setFavOnly(false);
  };

  const toggleFav = (id: string) => setFavs(toggleFavourite(id));

  /** Clicking a bucket narrows the query to it, when that dimension is filterable. */
  const applyBucket = (bucket: string) => {
    if (groupBy === 'ticker') setField('ticker', bucket);
    else if (groupBy === 'option_type') setField('optionType', bucket);
    else if (groupBy === 'side') setField('side', bucket);
    else if (groupBy === 'expiry') setField('expiry', bucket);
  };

  const bucketClickable = ['ticker', 'option_type', 'side', 'expiry'].includes(groupBy);

  const total = page?.total ?? 0;
  const totalExact = page?.totalExact ?? false;
  const rangeFrom = total === 0 ? 0 : offset + 1;
  const rangeTo = offset + rowsAll.length;
  const hasPrev = offset > 0;
  const hasNext = rowsAll.length >= pageSize && (totalExact ? offset + pageSize < total : true);
  const clientFiltered = plan.client.length > 0 || favOnly;

  const activeFilterCount = useMemo(
    () => FILTER_KEYS.filter((k) => String(query[k] ?? '').trim() !== '').length + rules.filter((r) => r.value.trim()).length,
    [query, rules],
  );

  const num = (label: string, key: keyof FlowTapeQuery, placeholder: string) => (
    <div className="fg">
      <span className="lab">{label}</span>
      <div className="ctl">
        <input
          className="num mono"
          type="number"
          placeholder={placeholder}
          value={String(query[key] ?? '')}
          onChange={(e) => setField(key, e.target.value)}
        />
      </div>
    </div>
  );

  const ds = isDesignSystemOn();
  const liveLabel = !live
    ? 'Live off'
    : livePausedForPaging
      ? 'Live paused · back to page 1 to resume'
      : `Live · every ${POLL_MS / 1000}s`;

  return (
    <div className={`rtlscan flowtape${simpleLayout ? ' simple' : ''}${ds ? ' mmec' : ''}`}>
      <div className="head">
        <div>
          <h1>
            Options Tape <span>· Live Tape</span>
          </h1>
          <p className="ftsub" style={{ margin: '2px 0 0', fontSize: 12, opacity: 0.7 }}>
            Every options print — start from a preset, or set your own filters and save them
          </p>
        </div>
        <div className="hr">
          <div className="vtoggle" role="group" aria-label="View">
            <button type="button" className={view === 'prints' ? 'on' : ''} onClick={() => setView('prints')}>
              Prints
            </button>
            <button
              type="button"
              className={view === 'contracts' ? 'on' : ''}
              onClick={() => setView('contracts')}
              title={`Top ${CONTRACT_BOARD_LIMIT} contracts by premium summed over every matching print`}
            >
              Contracts
            </button>
          </div>
          <button
            type="button"
            className={`chip ftlive${live ? ' on' : ''}${livePausedForPaging ? ' paused' : ''}`}
            onClick={() => setLive((v) => !v)}
            aria-pressed={live}
            title={`${liveLabel}. Skipped while this tab is hidden.`}
          >
            <span className="dot" aria-hidden="true" />
            {live ? (livePausedForPaging ? 'Live paused' : 'Live') : 'Live off'}
          </button>
          <button
            type="button"
            className={`chip ftfav${favOnly ? ' on' : ''}`}
            onClick={() => setFavOnly((v) => !v)}
            title="Show starred contracts only (filters the loaded rows)"
          >
            ★ Favourites{favs.size ? ` (${favs.size})` : ''}
          </button>
          <button
            type="button"
            className={`chip${alertsOpen ? ' on' : ''}`}
            onClick={() => setAlertsOpen((v) => !v)}
            title="Alert rules for the tape"
          >
            🔔 Alerts
          </button>
          <span className="status">
            {loading ? (
              'Querying…'
            ) : view === 'contracts' ? (
              <>
                <b>{contracts.length.toLocaleString()}</b>&nbsp;contracts · top {CONTRACT_BOARD_LIMIT} by premium
              </>
            ) : (
              <>
                <b>{totalExact ? total.toLocaleString() : `~${total.toLocaleString()}`}</b>&nbsp;matching prints
                {page ? ` · ${page.persistedTotal.toLocaleString()} stored` : ''}
              </>
            )}
            {live && lastLoaded && !loading ? ` · ${lastLoaded.toISOString().slice(11, 19)} UTC` : ''}
          </span>
        </div>
      </div>

      <PresetsRow
        activeId={activePresetId}
        baseName={baseName}
        userPresets={userPresets}
        onApply={applyPreset}
        onSave={savePreset}
        onRename={(id, name) => storeUserPresets(userPresets.map((p) => (p.id === id ? { ...p, name } : p)))}
        onDelete={(id) => storeUserPresets(userPresets.filter((p) => p.id !== id))}
        onImport={(list) => storeUserPresets([...userPresets, ...list])}
      />

      <div className="bar">
        <div className="fg">
          <span className="lab">Ticker</span>
          <div className="ctl">
            <input
              className="num mono search"
              type="text"
              placeholder="exact…"
              value={query.ticker ?? ''}
              onChange={(e) => setField('ticker', e.target.value.toUpperCase())}
            />
          </div>
        </div>
        <div className="fg">
          <span className="lab">Contains</span>
          <div className="ctl">
            <input
              className="num mono search"
              type="text"
              placeholder="partial…"
              value={query.search ?? ''}
              disabled={Boolean(query.ticker)}
              title={query.ticker ? 'Ignored while an exact ticker is set' : ''}
              onChange={(e) => setField('search', e.target.value.toUpperCase())}
            />
          </div>
        </div>
        <div className="fg">
          <span className="lab">Underlying</span>
          <div className="ctl">
            {ASSET_CLASS_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                className={`chip${query.underlyingClass === o.value ? ' on' : ''}`}
                title="Filtered in QuestDB across the whole match set"
                onClick={() => setField('underlyingClass', query.underlyingClass === o.value ? '' : o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div className="fg">
          <span className="lab">Cap tier</span>
          <div className="ctl">
            {CAP_TIER_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                className={`chip${query.capTier === o.value ? ' on' : ''}`}
                title="Market cap from Polygon, applied in QuestDB across the whole match set"
                onClick={() => setField('capTier', query.capTier === o.value ? '' : o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div className="fg">
          <span className="lab">Side</span>
          <div className="ctl">
            {['Ask', 'Bid', 'Mid'].map((s) => (
              <button
                key={s}
                type="button"
                className={`chip${query.side === s ? ' on' : ''}`}
                onClick={() => setField('side', query.side === s ? '' : s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="fg">
          <span className="lab">Type</span>
          <div className="ctl">
            {[
              ['C', 'Call'],
              ['P', 'Put'],
            ].map(([v, label]) => (
              <button
                key={v}
                type="button"
                className={`chip${query.optionType === v ? ' on' : ''}`}
                onClick={() => setField('optionType', query.optionType === v ? '' : v)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="fg">
          <span className="lab">Premium ≥</span>
          <div className="ctl">
            {[
              ['100000', '$100K'],
              ['1000000', '$1M'],
              ['5000000', '$5M'],
            ].map(([v, label]) => (
              <button
                key={v}
                type="button"
                className={`chip${query.minPremium === v ? ' on' : ''}`}
                onClick={() => setField('minPremium', query.minPremium === v ? '' : v)}
              >
                {label}
              </button>
            ))}
            <input
              className="num mono"
              type="number"
              placeholder="$…"
              value={query.minPremium ?? ''}
              onChange={(e) => setField('minPremium', e.target.value)}
            />
          </div>
        </div>
        {num('Premium ≤', 'maxPremium', '$…')}
        {num('Size ≥', 'minSize', 'contracts…')}
        {num('Strike ≥', 'minStrike', 'strike…')}
        {num('Strike ≤', 'maxStrike', 'strike…')}
        {num('DTE ≥', 'minDte', 'days…')}
        {num('DTE ≤', 'maxDte', 'days…')}
        <div className="fg">
          <span className="lab">Moneyness</span>
          <div className="ctl">
            {FLOW_MONEYNESS_OPTIONS.map((m) => (
              <button
                key={m}
                type="button"
                className={`chip${query.moneyness === m ? ' on' : ''}`}
                title="Computed server-side from the Polygon underlying price stamped on each print"
                onClick={() => setField('moneyness', query.moneyness === m ? '' : m)}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
        <div className="fg">
          <span className="lab">Expiry</span>
          <div className="ctl">
            <input
              className="num mono"
              type="date"
              value={query.expiry ?? ''}
              onChange={(e) => setField('expiry', e.target.value)}
            />
          </div>
        </div>
        <div className="fg">
          <span className="lab">Session</span>
          <div className="ctl">
            {ROLLING_WINDOWS.map((w) => (
              <button
                key={w.minutes}
                type="button"
                className={`chip${rollingMinutes === w.minutes ? ' on' : ''}`}
                title={`Rolling: the last ${w.label}, moved forward on every live refresh`}
                onClick={() => applyRolling(w.minutes)}
              >
                {w.label}
              </button>
            ))}
            {TIME_WINDOWS.map((w) => (
              <button
                key={w.id}
                type="button"
                className={`chip${!rollingMinutes && timeWindow === w.id ? ' on' : ''}`}
                title={describeWindow(w.id)}
                onClick={() => applyWindow(w.id)}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
        <div className="fg">
          <span className="lab">From (UTC)</span>
          <div className="ctl">
            <input
              className="num mono"
              type="datetime-local"
              value={isoToUtcInput(query.fromTs)}
              onChange={(e) => { setTimeWindow(''); setRollingMinutes(null); setField('fromTs', utcInputToIso(e.target.value)); }}
            />
          </div>
        </div>
        <div className="fg">
          <span className="lab">To (UTC)</span>
          <div className="ctl">
            <input
              className="num mono"
              type="datetime-local"
              value={isoToUtcInput(query.toTs)}
              onChange={(e) => { setTimeWindow(''); setRollingMinutes(null); setField('toTs', utcInputToIso(e.target.value)); }}
            />
          </div>
        </div>
        <div className="fg">
          <span className="lab">Sort</span>
          <div className="ctl">
            <select className="sel" value={sortBy} disabled={view === 'contracts'} title={view === 'contracts' ? 'Contracts are ranked by summed premium' : undefined} onChange={(e) => { setSortBy(e.target.value); setOffset(0); }}>
              {FLOW_SORT_FIELDS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="dirbtn"
              title="toggle direction"
              onClick={() => { setSortDir((d) => (d === 'desc' ? 'asc' : 'desc')); setOffset(0); }}
            >
              {sortDir === 'desc' ? '↓' : '↑'}
            </button>
          </div>
        </div>
        {view === 'contracts' && (
          <div className="fg">
            <span className="lab">
              Verdict <span className="ftwhere client-side">client-side</span>
            </span>
            <div className="ctl">
              {VERDICT_CHIPS.map((v) => (
                <button
                  key={v.value}
                  type="button"
                  className={`chip${verdict === v.value ? ' on' : ''}`}
                  title={`${v.title}. Read from the top ${CONTRACT_BOARD_LIMIT} contracts the server returned.`}
                  onClick={() => setVerdict(verdict === v.value ? '' : v.value)}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <button type="button" className="reset" onClick={resetAll} title="Back to All prints">
          Reset{activeFilterCount ? ` (${activeFilterCount})` : ''}
        </button>
      </div>


      <RuleBuilder
        rules={rules}
        serverIds={plan.server}
        contractsView={view === 'contracts'}
        onChange={(next) => { setRules(next); setOffset(0); }}
        newId={newRuleId}
      />

      <div className="ftgrid">
        <aside className="ftaside">
          <div className="cfhead">
            <span className="lab">Breakdown</span>
            <select className="sel" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
              {FLOW_GROUP_FIELDS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <div className="cfhint">
            Grouped in QuestDB over every matching print, not just this page.
          </div>
          {bucketsLoading && (ds ? <ToolState kind="loading" rows={4} /> : <div className="empty">Grouping…</div>)}
          {!bucketsLoading && !buckets.length && (ds ? <ToolState kind="empty" title="No buckets." body="" /> : <div className="empty">No buckets.</div>)}
          {!bucketsLoading && buckets.length > 0 && (
            <table className="tbl ftbuckets">
              <thead>
                <tr>
                  <th>{FLOW_GROUP_FIELDS.find((f) => f.key === groupBy)?.label ?? 'Bucket'}</th>
                  <th className="num">Premium</th>
                  <th className="num">Prints</th>
                </tr>
              </thead>
              <tbody>
                {buckets.map((b) => (
                  <tr
                    key={b.bucket}
                    className={bucketClickable ? 'clickable' : ''}
                    onClick={bucketClickable ? () => applyBucket(b.bucket) : undefined}
                    title={bucketClickable ? `Filter by ${b.bucket}` : undefined}
                  >
                    <td className="c-t">{b.bucket}</td>
                    <td className="mono num">{formatPremium(b.premium)}</td>
                    <td className="mono num">{b.prints.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </aside>

        <section className="ftmain">
          {error && (ds ? <ToolState kind="error" title={error} body="" /> : <div className="ftError">{error}</div>)}
          {view === 'contracts' ? (
            <ContractsTable
              rows={contracts}
              loadedCount={contractRows.length}
              loading={loading}
              favs={favs}
              onToggleFav={toggleFav}
              onOpen={(c) =>
                openHistorical(
                  { underlying: c.ticker, strike: c.strike, expiry: c.expiry, optionType: c.optionType === 'P' ? 'PUT' : 'CALL', source: 'volume' },
                  { compactChrome: simpleLayout },
                )
              }
            />
          ) : (
          <>
          <div className="tablewrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th className="th-star" aria-label="Favourite" />
                  <th>Time</th>
                  <th>Ticker</th>
                  <th>Type</th>
                  <th className="num">Strike</th>
                  <th>Expiry</th>
                  <th className="num">DTE</th>
                  <th>Side</th>
                  <th className="num">Premium</th>
                  <th className="num">Size</th>
                  <th className="num">Price</th>
                  <th>Exch</th>
                </tr>
              </thead>
              <tbody>
                {loading && !rows.length && (
                  <tr>
                    <td colSpan={12}>
                      {ds ? <ToolState kind="loading" rows={8} /> : <div className="empty">Querying the tape…</div>}
                    </td>
                  </tr>
                )}
                {!loading && !rows.length && !error && (
                  <tr>
                    <td colSpan={12}>
                      <div className="empty">
                        {ds ? (
                          <ToolState
                            kind="empty"
                            title={rowsAll.length ? 'No rows on this page pass the client-side rules or favourites.' : 'No prints match these filters.'}
                            body={rowsAll.length
                              ? 'Try the next page or widen them.'
                              : rollingMinutes
                                ? `No prints in the last ${rollingMinutes} minutes — the market may be closed. Try Last session.`
                                : ''}
                          />
                        ) : rowsAll.length
                          ? 'No rows on this page pass the client-side rules or favourites. Try the next page or widen them.'
                          : rollingMinutes
                            ? `No prints in the last ${rollingMinutes} minutes — the market may be closed. Try Last session.`
                            : 'No prints match these filters.'}
                      </div>
                    </td>
                  </tr>
                )}
                {rows.map((r) => {
                  const c = sideClass(r.side);
                  const hp = historicalParams(r);
                  return (
                    <tr
                      key={r.id}
                      className={c}
                      tabIndex={hp ? 0 : undefined}
                      title={hp ? `${r.ticker} $${r.strike} ${r.expiry} — open contract history` : undefined}
                      onClick={hp ? () => openHistorical(hp, { compactChrome: simpleLayout }) : undefined}
                    >
                      <td>
                        <button
                          type="button"
                          className="tstar"
                          aria-label={favs.has(rowFavKey(r)) ? 'Remove from favourites' : 'Add to favourites'}
                          onClick={(e) => { e.stopPropagation(); toggleFav(rowFavKey(r)); }}
                        >
                          {favs.has(rowFavKey(r)) ? '★' : '☆'}
                        </button>
                      </td>
                      <td className="mono">{formatTapeTime(r.time)}</td>
                      <td className="c-t">{r.ticker}</td>
                      <td>
                        <span className={`tbadge ${c}`}>{r.optionType === 'P' ? 'Put' : 'Call'}</span>
                      </td>
                      <td className="mono c-strike num">{r.strike == null ? '—' : `$${r.strike}`}</td>
                      <td className="mono">{r.expiry || '—'}</td>
                      <td className="mono num">{r.dte == null ? '—' : `${r.dte}d`}</td>
                      <td className={`mono ftside ftside--${c}`}>{r.side || '—'}</td>
                      <td className="mono num">{formatPremium(r.premium)}</td>
                      <td className="mono num">{r.size == null ? '—' : r.size.toLocaleString()}</td>
                      <td className="mono num">{r.price == null ? '—' : r.price.toFixed(2)}</td>
                      <td className="mono">{r.exchange || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="ftpager">
            <span className="count">
              {total === 0 ? (
                'no matches'
              ) : (
                <>
                  showing <b>{rangeFrom.toLocaleString()}</b>–<b>{rangeTo.toLocaleString()}</b> of{' '}
                  <b>{total.toLocaleString()}</b>
                  {!totalExact && ' (approximate)'}
                  {clientFiltered && (
                    <>
                      {' '}· <b>{rows.length}</b> on this page pass client-side rules
                    </>
                  )}
                </>
              )}
            </span>
            <div className="ctl">
              <select
                className="sel"
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setOffset(0); }}
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s} / page
                  </option>
                ))}
              </select>
              <button type="button" className="chip" disabled={!hasPrev} onClick={() => setOffset(0)}>
                « First
              </button>
              <button
                type="button"
                className="chip"
                disabled={!hasPrev}
                onClick={() => setOffset((o) => Math.max(0, o - pageSize))}
              >
                ‹ Prev
              </button>
              <button
                type="button"
                className="chip"
                disabled={!hasNext}
                onClick={() => setOffset((o) => o + pageSize)}
              >
                Next ›
              </button>
            </div>
          </div>

          </>
          )}

          <div className="note">
            Colour is <b>direction</b> from the tape side: at the ask = buying (green), at the bid = selling
            (red), mid is undirected. Click a {view === 'contracts' ? 'contract' : 'print'} to open its{' '}
            <b>contract history</b>. Bar filters, sorting, counts and grouping run in QuestDB over the full match
            set; rules marked <b>client-side</b>, the verdict chips and Favourites filter the rows already loaded.
            {FLOW_UNAVAILABLE_FILTERS.length > 0 && (
              <>
                {' '}Not offered yet:{' '}
                {FLOW_UNAVAILABLE_FILTERS.map((f, i) => (
                  <React.Fragment key={f.label}>
                    {i > 0 && ', '}
                    <b>{f.label}</b> ({f.reason})
                  </React.Fragment>
                ))}
                .
              </>
            )}
          </div>
        </section>
      </div>

      {alertsOpen && (
        <div className="mmecAlertDrawer">
          <div className="vtoggle ftalertsrc" role="group" aria-label="Alert source">
            <button type="button" className={alertSourceId === 'flow-tape' ? 'on' : ''} onClick={() => setAlertSourceId('flow-tape')}>
              Prints
            </button>
            <button type="button" className={alertSourceId === 'smart-flow' ? 'on' : ''} onClick={() => setAlertSourceId('smart-flow')}>
              {SMART_FLOW_SOURCE.label}
            </button>
          </div>
          <AlertRulesPanel
            key={alertSourceId}
            source={alertSourceId === 'flow-tape' ? FLOW_TAPE_SOURCE : SMART_FLOW_SOURCE}
            onClose={() => setAlertsOpen(false)}
          />
        </div>
      )}
    </div>
  );
};

export default FlowTapeView;
